import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, User, CheckCircle2, Send } from "lucide-react";
import Reveal from "./Reveal";
import { SITE } from "@/data/content";
import { WhatsAppIcon } from "./WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuickForm() {
  const [f, setF] = useState({ name: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || f.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter your name and a valid phone number");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...f, property_type: "Callback request", source: "contact-section" });
      setSent(true);
      toast.success("Request received — we will call you back");
    } catch {
      toast.error("Could not submit. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center bg-navy rounded-sm p-10" data-testid="contact-form-success">
        <CheckCircle2 className="w-12 h-12 text-gold" />
        <p className="mt-4 font-serif text-2xl text-white font-semibold">Request received</p>
        <p className="mt-2 text-white/65 text-sm">Sunil Aggarwal's team will call you back shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="contact-quick-form" className="bg-navy rounded-sm p-8 sm:p-10 space-y-5">
      <h3 className="font-serif text-2xl text-white font-semibold">Request a Call Back</h3>
      {[
        { key: "name", ph: "Your name *", type: "text", tid: "contact-input-name" },
        { key: "phone", ph: "Phone number *", type: "tel", tid: "contact-input-phone" },
      ].map((fl) => (
        <input
          key={fl.key}
          type={fl.type}
          placeholder={fl.ph}
          data-testid={fl.tid}
          value={f[fl.key]}
          onChange={(e) => setF({ ...f, [fl.key]: e.target.value })}
          className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 px-4 py-3.5 text-sm outline-none focus:border-gold transition-colors duration-300"
        />
      ))}
      <textarea
        placeholder="Your message (optional)"
        rows="3"
        data-testid="contact-input-message"
        value={f.message}
        onChange={(e) => setF({ ...f, message: e.target.value })}
        className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 px-4 py-3.5 text-sm outline-none focus:border-gold transition-colors duration-300 resize-none"
      />
      <button type="submit" disabled={sending} data-testid="contact-submit-button" className="btn-gold w-full disabled:opacity-60 disabled:cursor-wait">
        {sending ? "Sending…" : (<><Send className="w-4 h-4" /> Request Call Back</>)}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <section id="contact" className="py-24 lg:py-32 bg-navy-2 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      <div className="container-luxe relative">
        <Reveal className="max-w-3xl">
          <p className="eyebrow !text-gold mb-4">Contact</p>
          <h2 className="font-serif font-semibold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Let's Discuss Your Dholera Investment
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <div className="space-y-8">
            <Reveal className="bg-white/[0.03] border border-white/10 rounded-sm p-8 space-y-5">
              <div className="flex items-start gap-4">
                <User className="w-5 h-5 text-gold mt-1 shrink-0" />
                <div>
                  <p className="font-serif text-xl text-white font-semibold">Paras Real Estate</p>
                  <p className="text-white/60 text-sm">Owner: Sunil Aggarwal</p>
                  <p className="text-white/60 text-sm flex items-center gap-2 mt-1"><MapPin className="w-3.5 h-3.5 text-gold" /> {SITE.location}</p>
                </div>
              </div>
              <a href={SITE.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-4 text-white hover:text-gold transition-colors duration-300">
                <span className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center rounded-sm"><Phone className="w-4 h-4 text-gold" /></span>
                <span className="font-medium">{SITE.phoneDisplay} <span className="block text-xs text-white/50">This number is of Sunil Aggarwal</span></span>
              </a>
              <a href={SITE.contact2.phoneHref} data-testid="contact-phone2-link" className="flex items-center gap-4 text-white hover:text-gold transition-colors duration-300">
                <span className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center rounded-sm"><Phone className="w-4 h-4 text-gold" /></span>
                <span className="font-medium">{SITE.contact2.phoneDisplay} <span className="block text-xs text-white/50">{SITE.contact2.name}</span></span>
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white hover:text-gold transition-colors duration-300 break-all">
                <span className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center rounded-sm shrink-0"><Mail className="w-4 h-4 text-gold" /></span>
                <span className="font-medium">{SITE.email}</span>
              </a>
              <p className="flex items-center gap-4 text-white/60 text-sm">
                <span className="w-10 h-10 bg-white/5 border border-white/15 flex items-center justify-center rounded-sm shrink-0"><Clock className="w-4 h-4 text-gold" /></span>
                Business hours: <span className="font-mono text-[11px] uppercase tracking-wide text-white/85">10:00 AM – 8:00 PM</span>
              </p>
              <div className="flex flex-wrap gap-4 pt-3">
                <a href={SITE.phoneHref} data-testid="contact-call-button" className="btn-gold flex-1 min-w-[150px]">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold bg-[#25D366] text-white hover:bg-[#1fb757] transition-colors duration-300"
                >
                  <WhatsAppIcon /> WhatsApp
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="clip-frame border border-gold/40 p-2">
              <iframe
                title="Paras Real Estate — Dholera Smart City, Gujarat on Google Maps"
                src={SITE.mapsEmbed}
                className="w-full h-[280px] grayscale-[0.25]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-google-maps-embed"
              />
            </Reveal>
          </div>

          <Reveal delay={0.15} y={40}>
            <QuickForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
